<template>
    <div class="gallery-media-list-editor">
        <form
            class="action-buttons"
            @submit.prevent="">
            <fieldset :disabled="disabled">

                <label class="form-button">
                    <input
                        type="file"
                        multiple
                        :accept="allowedImageTypes.join(',')"
                        @change="addImageMedia">
                    Add Image
                </label>

                <button
                    class="form-button"
                    type="button"
                    @click="$modal.show('add-video-modal')">
                    Add Vimeo
                </button>

                <button
                    class="form-button"
                    type="button"
                    @click="$modal.show('add-sketchfab-modal')">
                    Add Sketchfab
                </button>

                <label class="form-button">
                    <input
                        type="file"
                        multiple
                        @change="addMarmosetMedia">
                    Add Marmoset Viewer
                </label>

                <modal
                    name="add-video-modal"
                    classes="v-modal"
                    @before-open="resetAddVideoUrl">
                    <section class="panel">

                        <div class="panel-heading">
                            <h2>
                                Add Vimeo
                                <button
                                    type="button"
                                    class="clear-button float-right"
                                    @click="$modal.hide('add-video-modal')">
                                    <i class="fa fa-times"></i>
                                </button>
                            </h2>
                        </div>

                        <div class="panel-body">
                            <form @submit.prevent="addVideoMedia">
                                <div class="form-group">
                                    <label>Vimeo URL</label>
                                    <input
                                        type="text"
                                        class="form-control"
                                        placeholder="Video URL"
                                        v-model="addVideoUrl"
                                        @input="updateAddVideoUrlInfo">
                                </div>
                                <div
                                    class="form-group"
                                    v-if="!!addVideoUrlError">
                                    <p class="error">URL is invalid</p>
                                </div>
                                <div
                                    class="form-group"
                                    v-if="!!addVideoUrlInfo">
                                    <input
                                        type="submit"
                                        class="btn btn-success"
                                        value="Add Vimeo">
                                </div>
                            </form>
                        </div>
                    </section>
                </modal>

                <modal
                    name="add-sketchfab-modal"
                    classes="v-modal"
                    @before-open="resetAddSketchfabUrl">
                    <section class="panel">

                        <div class="panel-heading">
                            <h2>
                                Add Sketchfab
                                <button
                                    type="button"
                                    class="clear-button float-right"
                                    @click="$modal.hide('add-sketchfab-modal')">
                                    <i class="fa fa-times"></i>
                                </button>
                            </h2>
                        </div>

                        <div class="panel-body">
                            <form @submit.prevent="addSketchfabMedia">
                                <div class="form-group">
                                    <label>Sketchfab URL</label>
                                    <input
                                        type="text"
                                        class="form-control"
                                        placeholder="https://sketchfab.com/models/..."
                                        v-model="addSketchfabUrl"
                                        @input="updateAddSketchfabUrlInfo">
                                </div>
                                <div
                                    class="form-group"
                                    v-if="!!addSketchfabUrlError">
                                    <p class="error">URL is invalid</p>
                                </div>
                                <div
                                    class="form-group"
                                    v-if="!!addSketchfabUrlInfo">
                                    <input
                                        type="submit"
                                        class="btn btn-success"
                                        value="Add Sketchfab">
                                </div>
                            </form>
                        </div>
                    </section>
                </modal>

            </fieldset>
        </form>

        <ol
            class="media-list"
            v-if="media.length > 0">
            <li
                v-for="(mediaItem, index) in media"
                class="media-col"
                :key="mediaItem.url"
                :draggable="!disabled"
                @dragstart="dndMedia_start"
                @dragenter="dndMedia_enter"
                @dragleave="dndMedia_leave"
                @dragover="dndMedia_over"
                @dragend="dndMedia_end"
                @drop="dndMedia_drop">
                <div class="media">
                    <button
                        class="clear-button"
                        @click="removeMedia(index)">
                        <i class="fa fa-times"></i>
                    </button>

                    <div class="preview">
                        <div class="inner">
                            <img
                                :src="mediaItem.thumbnailUrl"
                                draggable="false">
                        </div>
                    </div>

                    <div class="media-fields">
                        <label>Caption (one word) *</label>
                        <textarea
                            class="form-control"
                            placeholder="Example: wide, closeup, boots, head, gun"
                            cols="40"
                            rows="2"                            
                            v-model="mediaItem.caption">
                        </textarea>
                        <label>Discipline(s)</label>
                        <select v-if="mediaItem.disciplines" @change="addDiscipline($event, index)" class="form-control">
                            <option default disabled selected>Select all that apply</option>
                            <option 
                                v-for="discipline in disciplines" 
                                :disabled="mediaItem.disciplines.includes(discipline)" 
                               >{{discipline}}</option>
                        </select> 
                        <ul class="disciplines">
                            <li v-for="(discipline, $disciplineIndex) in mediaItem.disciplines">
                                {{discipline}} 
                                <div class="buttons">
                                    <button @click.prevent="removeDiscipline(index, $disciplineIndex)" class="remove-discipline">x</button>
                                    <button @click.prevent="applyDisciplineToAll(discipline)" class="btn btn-xs apply-to-all">Apply to All</button>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </li>
        </ol>

        <div
            class="empty-area"
            v-else>
            <p>No media to show</p>
        </div>
    </div>
</template>

<script>
    import toastr from 'toastr';

    import Marmoset from '../../packages/marmoset';
    import Noembed from '../../services/noembed';
    import Sketchfab from '../../services/sketchfab';


    // Stolen from the html editor (we should probably centralize these
    // somewhere, like in a utils file).
    function checkFileTypeAgainstList(typeList, file) {
        for (let type of typeList) {
            type = type.trim();
            if (type.startsWith('.')) {
                if (file.name.endsWith(type)) {
                    return true;
                }
            } else {
                if (file.type === type) {
                    return true;
                }
            }
        }
        return false;
    }

    function validateFileByWhitelist(whitelist, file) {
        return checkFileTypeAgainstList(whitelist, file);
    }


    export default {
        name: 'gallery-media-list-editor',

        props: {
            disabled: {
                type: Boolean,
                default: false
            },
            value: {
                type: Array,
                // [ { id, url, caption, disciplines, file, thumbnailUrl } ]
                // v-model is supported
            },
            disciplines: {
                type: Array
            }
        },

        data() {
            return {
                media: [], // Synced with `value` prop if prop is provided

                // When reordering the media through drag and drop, we need to
                // keep track of the original item that we're dragging (only the
                // target element/drop zone is available through the 'drop'
                // event).
                mediaElemDragged: null,

                // When adding a new video, we accept YouTube URLs which then
                // need to be verified. We'll use these vars to store the link,
                // as well as the info found at the link (using noembed.com).
                addVideoUrl: null,
                addVideoUrlInfo: null,
                addVideoUrlError: null,

                addSketchfabUrl: null,
                addSketchfabUrlInfo: null,
                addSketchfabUrlError: null,
            };
        },

        computed: {
            allowedImageTypes() {
                // This was also stolen from the html editor (see above).
                return [
                    'image/apng',
                    'image/bmp',
                    'image/gif',
                    'image/jpeg',
                    'image/png',
                    'image/webp',
                ];
            }
        },

        beforeDestroy() {
            this.media.forEach(media => {
                if (media.type === 'marmoset') {
                    URL.revokeObjectURL(media.thumbnailUrl);
                    media.thumbnailUrl = null;
                }
            });
        },

        methods: {
            addDiscipline(e, index) {        
                this.media[index].disciplines.push(e.target.value);
            },
            applyDisciplineToAll(discipline) {
                for (let asset of this.media) {
                    if (!asset.disciplines.includes(discipline)) {
                        asset.disciplines.push(discipline);
                    }
                }
            },
            removeDiscipline(mediaIndex, disciplineIndex) {
                this.media[mediaIndex].disciplines.splice(disciplineIndex, 1);
            },
            resetAddVideoUrl() {
                this.addVideoUrl = null;
                this.addVideoUrlInfo = null;
                this.addVideoUrlError = null;
            },

            async updateAddVideoUrlInfo() {
                this.addVideoUrlInfo = null;
                this.addVideoUrlError = null;
               
                if (!this.addVideoUrl) {
                    return;
                }

                await Noembed.getInfo(this.addVideoUrl).then(info => {
                    if (info.providerName.match(/Vimeo/i)) {
                        this.addVideoUrlInfo = info;
                    } else {
                        this.addVideoUrlError = 'Invalid provider';
                    }
                }, err => {
                    this.addVideoUrlError = err;
                });
            },

            resetAddSketchfabUrl() {
                this.addSketchfabUrl = null;
                this.addSketchfabUrlInfo = null;
                this.addSketchfabUrlError = null;
            },

            async updateAddSketchfabUrlInfo() {
                this.addSketchfabUrlInfo = null;
                this.addSketchfabUrlError = null;
                if (!this.addSketchfabUrl) {
                    return;
                }

                await Sketchfab.getInfo(this.addSketchfabUrl).then(info => {
                    this.addSketchfabUrlInfo = info;
                }, err => {
                    this.addSketchfabUrlError = err;
                });
            },

            dndMedia_start(e) {
                e.stopPropagation();
                this.mediaElemDragged = e.target;
                e.dataTransfer.dropEffect = 'move';

                // Requried for Firefox
                e.dataTransfer.setData('text/plain', 'dummy');
            },

            dndMedia_over(e) {
                e.preventDefault();
                if (e.target.classList.contains('media-col')) {
                    e.dataTransfer.dropEffect = 'move';
                } else {
                    e.dataTransfer.dropEffect = 'none';
                }
            },

            dndMedia_enter(e) {
                e.target.classList.add('drop-zone');
            },

            dndMedia_leave(e) {
                e.target.classList.remove('drop-zone');
            },

            dndMedia_drop(e) {
                e.preventDefault();

                // Find the indices of the elements to be swapped
                const listElem = this.mediaElemDragged.parentNode;
                const listElemChildren = Array.from(listElem.children);
                const index1 = listElemChildren.indexOf(this.mediaElemDragged);
                const index2 = listElemChildren.indexOf(e.target);
                if (index1 < 0 || index2 < 0 || index1 === index2) {
                    return;
                }

                this.swapMedia(index1, index2);
            },

            dndMedia_end(e) {
                const listElem = this.mediaElemDragged.parentNode;
                const listElemChildren = Array.from(listElem.children);
                listElemChildren.forEach(c => c.classList.remove('drop-zone'));
                this.mediaElemDragged = null;
            },

            async addImageMedia(e) {
                const files = Array.from(e.target.files);

                // Validate against whitelist
                for (const file of files) {
                    const fileIsAllowed = validateFileByWhitelist(
                        this.allowedImageTypes,
                        file,
                    );
                    if (!fileIsAllowed) {
                        toastr.error(`The file "${file.name}" is not allowed.`);
                        return;
                    }
                }

                // Instead of uploading the images directly to S3, we'll load
                // them into the browser cache using URL.createObjectURL(). This
                // will be much faster for the user so that they can get to
                // organizing the media and filling out the other gallery info.
                // It also avoids the problem where a user uploads some media,
                // and then closes the new gallery form without submitting, and
                // having that untracked data in S3.
                for (const file of files) {
                    await this.addMedia({
                        type: 'image',
                        url: URL.createObjectURL(file),
                        caption: '',
                        disciplines: [],
                        thumbnailUrl: null,
                        file, // Needed when uploading the file later
                    });
                }
            },

            async addVideoMedia() {
                this.$modal.hide('add-video-modal');

                await this.addMedia({
                    type: 'video',
                    url: this.addVideoUrl,
                    caption: '',
                    disciplines: [],
                    thumbnailUrl: null
                });
            },

            async addSketchfabMedia() {
                this.$modal.hide('add-sketchfab-modal');

                await this.addMedia({
                    type: 'sketchfab',
                    url: this.addSketchfabUrl,
                    caption: '',
                    thumbnailUrl: null
                });
            },

            async addMarmosetMedia(e) {
                const files = Array.from(e.target.files);

                // See comment in addImageMedia().
                for (const file of files) {
                    await this.addMedia({
                        type: 'marmoset',
                        url: URL.createObjectURL(file),
                        caption: '',
                        disciplines: [],
                        thumbnailUrl: null,
                        file, // Needed when uploading the file later
                    });
                }
            },

            async addMedia(mediaItem) {
                this.media.push(mediaItem);
                this.$emit('input', this.media);

                // Make sure the media has a thumbnail
                mediaItem = this.media.slice(-1)[0];
                if (!mediaItem.thumbnailUrl) {
                    try {
                        const url = await this.getMediaThumbUrl(mediaItem);
                        mediaItem.thumbnailUrl = url;
                        this.triggerRender();
                    } catch (err) {
                        console.log('Error loading thumbnail:', err);
                        mediaItem.error = err;
                    }
                }
            },

            removeMedia(index) {
                // Remove the element in a way so that Vue will detect the
                // change.
                this.media.splice(index, 1);
                this.$emit('input', this.media);
            },

            swapMedia(index1, index2) {
                // Swap the elements in a way so that Vue will detect the
                // change.
                const item1 = this.media[index1];
                const item2 = this.media[index2];
                this.media.splice(index1, 1, item2);
                this.media.splice(index2, 1, item1);
                this.$emit('input', this.media);
            },

            async getMediaThumbUrl(mediaItem) {
                const { type, url } = mediaItem;
                switch (type) {
                case 'image':
                    return url;
                case 'video':
                    return (await Noembed.getInfo(url)).thumbnailUrl;
                case 'sketchfab':
                    return (await Sketchfab.getInfo(url)).thumbnailUrl;
                case 'marmoset':
                    return URL.createObjectURL(
                        await Marmoset.createThumbnailBlob(url)
                    );
                default:
                    return null;
                }
            },

            triggerRender() {
                this.media.splice(0, 0);
            }
        },

        watch: {
            value: {
                immediate: true,
                handler: function(value) {
                    this.media = value;

                    // Make sure each media item has a thumbnail
                    this.media.forEach(async mediaItem => {
                        if (mediaItem.thumbnailUrl) {
                            return;
                        }
                        try {
                            const url = await this.getMediaThumbUrl(mediaItem);
                            mediaItem.thumbnailUrl = url;
                            this.triggerRender();
                        } catch (err) {
                            console.log('Error loading thumbnail:', err);
                            mediaItem.error = err;
                        }
                    });
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    .float-right {
        float: right;
    }

    .clear-button {
        border: none;
        background: none;
    }

    .form-button {
        background: #33ccff;
        border: #33ccff solid 1px;
        border-radius: 0;
        box-shadow: none;
        box-sizing: border-box;
        color: black;
        display: inline-block;
        font-weight: 400 !important;
        height: 36px;
        line-height: 36px;
        padding: 0 20px;
        text-decoration: none;
        text-shadow: none;
        top: 0; // For when using labels to wrap hidden file inputs
        &:hover {
            background: black;
            color: white;
            border: solid 1px #33ccff;
            outline-offset: 0;
        }
    }

    .action-buttons {
        margin-bottom: 10px;
    }

    .v-modal {
        .panel {
            background-color: #222222 !important;
        }
        .panel-heading i {
            color: #33ccff;
        }
        form {
            margin: 20px;
        }
        .form-group {
            margin-bottom: 30px;
        }
    }

    .empty-area {
        display: table;
        height: 100px;
        width: 100%;
        text-align: center;
        position: relative;

        > p {
            display: table-cell;
            font-style: italic;
            vertical-align: middle;
        }
    }

    input[type=file] {
        display: none;
    }

    .media-list {
        padding: 0;
        margin: 0;
        display: flex;
        flex-wrap:wrap;
    }

    ul.disciplines {
        margin-top:1rem;
        color:#3cf;
        li {
            display:flex;
            align-items: center;
            justify-content:space-between;
            margin-bottom:.5rem;
        }
        .apply-to-all {
            background:#3cf;
            color:#222;
        }
        .remove-discipline {
            background:transparent;
            position:relative;
            top:2px;
            color:#3cf;
            border:none;
            boxs-shadow:none;
        }
    }
    .media-col {
        display: inline-block;
        width: 33%;
        padding: 20px 10px 0 10px;
        &.drop-zone {
            * {
                pointer-events: none;
            }
            .media {
                border: 2px dashed #aaa;
            }
        }
        * {
            pointer-events: none;
        }
        button, textarea, select {
            pointer-events: auto;
        }
    }

    .media {
        border: 1px solid #aaa;
        position: relative;
        button.clear-button {
            background-color: rgba(0, 0, 0, 0.4);
            position: absolute;
            right: 5px;
            top: 5px;
            width: 20px;
            height: 20px;
            line-height: 20px;
            text-align: center;
            z-index: 1;
            padding: 0;
        }
        .preview {
            background-color: black;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            overflow: hidden;
        }
        .preview::before {
            content: '';
            display: inline-block;
            padding-top: 100%;
            float: left;
        }
        .preview::after {
            content: '';
            display: table;
            clear: both;
        }
        .preview .inner {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        .preview img {
            max-width: 100%;
            max-height: 100%;
        }
    }

    .media-fields {
        margin: 20px 20px 30px 20px;
        textarea {
            resize:none;
            margin-bottom:1rem;
        }
    }
</style>

<style lang="scss">
    .gallery-media-list-editor {
        .v-modal .error {
            font-style: italic;
        }
    }
</style>



// WEBPACK FOOTER //
// GalleryMediaListEditor.vue?aee426ca